import { useEffect } from "react";
import { SITE_NAME, DEFAULT_TITLE, DEFAULT_DESCRIPTION, getCanonicalUrl, getOgImageUrl, OG_IMAGE_WIDTH, OG_IMAGE_HEIGHT } from "@/config/seo";

interface SEOHeadProps {
  title?: string;
  description?: string;
  path: string;
  image?: string;
  type?: "website" | "article";
  publishedTime?: string;
  modifiedTime?: string;
  noindex?: boolean;
}

const SEOHead = ({
  title,
  description,
  path,
  image,
  type = "website",
  publishedTime,
  modifiedTime,
  noindex = false,
}: SEOHeadProps) => {
  useEffect(() => {
    const fullTitle = title ? `${title} - ${SITE_NAME}` : DEFAULT_TITLE;
    const metaDescription = description || DEFAULT_DESCRIPTION;
    const canonicalUrl = getCanonicalUrl(path);
    const ogImageUrl = getOgImageUrl(image);

    // Update document title
    document.title = fullTitle;

    // Helper to update or create meta tag
    const updateMeta = (selector: string, attribute: string, value: string) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement("meta");
        if (selector.includes("property=")) {
          element.setAttribute("property", selector.match(/property="([^"]+)"/)?.[1] || "");
        } else if (selector.includes("name=")) {
          element.setAttribute("name", selector.match(/name="([^"]+)"/)?.[1] || "");
        }
        document.head.appendChild(element);
      }
      element.setAttribute(attribute, value);
    };

    // Helper to update or create link tag
    const updateLink = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement("link");
        element.setAttribute("rel", rel);
        document.head.appendChild(element);
      }
      element.setAttribute("href", href);
    };

    // Update canonical URL
    updateLink("canonical", canonicalUrl);

    // Update meta description
    updateMeta('meta[name="description"]', "content", metaDescription);

    // Update robots meta
    const robotsContent = noindex 
      ? "noindex, nofollow, max-image-preview:large" 
      : "index, follow, max-image-preview:large";
    updateMeta('meta[name="robots"]', "content", robotsContent);

    // Update Open Graph tags
    updateMeta('meta[property="og:title"]', "content", fullTitle);
    updateMeta('meta[property="og:description"]', "content", metaDescription);
    updateMeta('meta[property="og:url"]', "content", canonicalUrl);
    updateMeta('meta[property="og:image"]', "content", ogImageUrl);
    updateMeta('meta[property="og:type"]', "content", type);

    // Update Twitter Card tags
    updateMeta('meta[name="twitter:title"]', "content", fullTitle);
    updateMeta('meta[name="twitter:description"]', "content", metaDescription);
    updateMeta('meta[name="twitter:image"]', "content", ogImageUrl);

    // Update OG image dimensions (important for Discover)
    updateMeta('meta[property="og:image:width"]', "content", String(OG_IMAGE_WIDTH));
    updateMeta('meta[property="og:image:height"]', "content", String(OG_IMAGE_HEIGHT));
    updateMeta('meta[property="og:image:alt"]', "content", fullTitle);

    // Add article-specific meta tags
    if (type === "article") {
      if (publishedTime) {
        updateMeta('meta[property="article:published_time"]', "content", publishedTime);
      }
      if (modifiedTime) {
        updateMeta('meta[property="article:modified_time"]', "content", modifiedTime);
      }
    }

    // Cleanup function
    return () => {
      document.title = DEFAULT_TITLE;
      
      const descMeta = document.querySelector('meta[name="description"]');
      if (descMeta) descMeta.setAttribute("content", DEFAULT_DESCRIPTION);
      
      const canonicalLink = document.querySelector('link[rel="canonical"]');
      if (canonicalLink) canonicalLink.setAttribute("href", getCanonicalUrl("/"));
      
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) ogTitle.setAttribute("content", DEFAULT_TITLE);
      
      const ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) ogDesc.setAttribute("content", DEFAULT_DESCRIPTION);
      
      const ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl) ogUrl.setAttribute("content", getCanonicalUrl("/"));
      
      const ogImage = document.querySelector('meta[property="og:image"]');
      if (ogImage) ogImage.setAttribute("content", getOgImageUrl());
      
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta) robotsMeta.setAttribute("content", "index, follow, max-image-preview:large");
    };
  }, [title, description, path, image, type, publishedTime, modifiedTime, noindex]);

  return null;
};

export default SEOHead;
